import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Select from 'react-select';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Swal from 'sweetalert2';
import { fetchUsers, insertSalesPersonVisit } from 'Service/ApiService';

const APP_RED = '#e31e24';
const APP_DARK = '#121212';
const BRAND_RED = '#7a2a24';

const brandOptionSx = {
  m: 0,
  px: 1.25,
  py: 0.25,
  border: '1px solid #e6ddcc',
  borderRadius: '20px',
  bgcolor: '#fbf9f5',
  '& .MuiCheckbox-root': {
    p: 0.5,
    mr: 0.25,
    color: '#7b7b7b',
    '&.Mui-checked': { color: BRAND_RED }
  },
  '& .MuiFormControlLabel-label': {
    color: `${APP_DARK} !important`,
    fontSize: 12.5,
    fontWeight: 500
  }
};

const radioIcon = <Box sx={{ width: 18, height: 18, border: `2px solid ${APP_DARK}`, borderRadius: '50%', bgcolor: '#fff' }} />;
const radioCheckedIcon = (
  <Box sx={{ width: 18, height: 18, border: `2px solid ${APP_RED}`, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: APP_RED }} />
  </Box>
);

const sections = [
  {
    title: 'Visit & Sales Person Details',
    fields: [
      { name: 'sales_person', label: 'SSML Secondary Sales Person Name', placeholder: 'e.g. Amit Kumar Sharma', required: true, xs: 12, sm: 6 },
      { name: 'location', label: 'Location / Address', placeholder: 'e.g. Tatanagar', xs: 12, sm: 6 },
      { name: 'pincode', label: 'Pincode', placeholder: '6-digit pincode', type: 'tel', inputProps: { pattern: '[0-9]{6}', maxLength: 6 }, xs: 12, sm: 6 }
    ]
  },
  {
    title: 'Retailer Details',
    fields: [
      { name: 'retailer_name', label: 'Retailer Name', placeholder: 'e.g. Mahabir Cloth Store', required: true },
      { name: 'place', label: 'Place', placeholder: 'e.g. Jugsalai' },
      { name: 'proprietor', label: 'Proprietor Name', placeholder: 'e.g. Rakesh Kumar Jain' },
      { name: 'mobile', label: 'Mobile No.', placeholder: '10-digit mobile number', type: 'tel', inputProps: { pattern: '[0-9]{10}', maxLength: 10 }, helperText: 'Enter a valid 10-digit mobile number' },
      { name: 'proprietor_email', label: 'Proprietor Email ID', placeholder: 'name@example.com', type: 'email' },
      { name: 'retailer_email', label: 'Retailer Email ID', placeholder: 'name@example.com', type: 'email' },
      { name: 'dob', label: 'Proprietor Date of Birth', type: 'date', inputLabelProps: { shrink: true } },
      { name: 'shop_area', label: 'Retailer Shop Outlet Area', placeholder: 'e.g. 500 Sq. Ft.' }
    ]
  }
];

const stockGroups = [
  { title: 'Total Stock in the Shop', fields: [['stock_total_shirting', 'Shirting (pcs)'], ['stock_total_suiting', 'Suiting (pcs)']] },
  { title: 'Total Shop Sale', fields: [['sale_daily', 'Daily'], ['sale_monthly', 'Monthly'], ['sale_yearly', 'Yearly'], ['sample_mill_dealer', 'Total Sample of Mill & Dealer']] },
  { title: 'Total SSML Brand Stock Displayed in the Shop', fields: [['ssml_stock_shirting', 'Shirting (pcs)'], ['ssml_stock_suiting', 'Suiting (pcs)']] },
  { title: 'Total Sale of SSML Brand', fields: [['ssml_sale_shirting', 'Shirting (pcs)'], ['ssml_sale_suiting', 'Suiting (pcs)']] },
  { title: 'Total White Thaan Stock in the Shop', fields: [['white_thaan_shop_shirting', 'Shirting (pcs)'], ['white_thaan_shop_suiting', 'Suiting (pcs)']] },
  { title: 'Total SSML White Thaan Stock in the Shop', fields: [['white_thaan_ssml_shirting', 'Shirting (pcs)'], ['white_thaan_ssml_suiting', 'Suiting (pcs)']] },
  { title: 'Total Black Thaan Stock in the Shop', fields: [['black_thaan_shop_shirting', 'Shirting (pcs)'], ['black_thaan_shop_suiting', 'Suiting (pcs)']] },
  { title: 'Total SSML Black Thaan Stock in the Shop', fields: [['black_thaan_ssml_shirting', 'Shirting (pcs)'], ['black_thaan_ssml_suiting', 'Suiting (pcs)']] }
];

const Field = ({ field, resetVersion }) => {
  const [error, setError] = useState('');
  const isMobile = field.name === 'mobile';
  const isPincode = field.name === 'pincode';
  const isEmail = field.type === 'email';

  useEffect(() => {
    setError('');
  }, [resetVersion]);

  const validate = (value) => {
    if (!value) {
      setError('');
      return true;
    }
    if (isMobile && !/^\d{10}$/.test(value)) {
      setError('Mobile number must contain exactly 10 digits.');
      return false;
    }
    if (isPincode && !/^\d{6}$/.test(value)) {
      setError('Pincode must contain exactly 6 digits.');
      return false;
    }
    if (isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setError('Enter a valid email address.');
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (event) => {
    if (isMobile || isPincode) {
      event.target.value = event.target.value.replace(/\D/g, '').slice(0, isMobile ? 10 : 6);
    }
    validate(event.target.value);
  };

  return (
    <Grid item xs={field.xs || 12} sm={field.sm || 6}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        name={field.name}
        label={
          field.required ? (
            <>
              {field.label} <Box component="span" sx={{ color: APP_RED }}>*</Box>
            </>
          ) : (
            field.label
          )
        }
        placeholder={field.placeholder}
        type={field.type || 'text'}
        error={Boolean(error)}
        helperText={error}
        onChange={handleChange}
        onBlur={(event) => validate(event.target.value)}
        inputProps={{
          ...field.inputProps,
          required: field.required || undefined,
          inputMode: isMobile || isPincode ? 'numeric' : undefined
        }}
        InputLabelProps={field.inputLabelProps}
      />
    </Grid>
  );
};

const SectionCard = ({ number, title, children }) => {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" mb={2.25}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: APP_RED, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 13 }}>{number}</Box>
          <Typography variant="h5" sx={{ color: APP_DARK, fontWeight: 600 }}>{title}</Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
};

const NumberGroup = ({ title, fields }) => (
  <Box>
    <Typography variant="overline" sx={{ color: APP_RED, fontWeight: 700, letterSpacing: 1 }}>{title}</Typography>
    <Grid container spacing={2} mt={-0.5}>
      {fields.map(([name, label]) => (
        <Grid item xs={12} sm={fields.length > 2 ? 3 : 6} key={name}>
          <TextField fullWidth size="small" type="number" name={name} label={label} inputProps={{ min: 0 }} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const SalesPersonVisit = () => {
  const formRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [resetVersion, setResetVersion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);
  const [salesPersonSearch, setSalesPersonSearch] = useState('');
  const [salesPersonLoading, setSalesPersonLoading] = useState(false);
  const [loggedInUserOption, setLoggedInUserOption] = useState(null);
  const [otherBrand, setOtherBrand] = useState(false);
  const previewUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : ''), [photo]);
  const loggedInUser = useMemo(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      const user = parsedUser && typeof parsedUser === 'object' ? parsedUser : {};
      return {
        userCode: String(user.userCode || ''),
        name: user.displayName || user.userName || ''
      };
    } catch (error) {
      return { userCode: '', name: '' };
    }
  }, []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setSalesPersonLoading(true);
      try {
        const response = await fetchUsers(salesPersonSearch.trim());
        if (active) {
          const options = response?.success === true && Array.isArray(response.data)
            ? response.data.map((row) => {
              const code = String(row.userCode || '');
              const name = row.displayName || row.userName || '';
              return {
                value: code,
                label: name && name !== code ? `${name} (${code})` : (name || code),
                name
              };
            }).filter((option) => option.value)
            : [];
          setSalesPersonOptions(options);
          if (!salesPersonSearch.trim() && loggedInUser.userCode) {
            const currentUserOption = options.find((option) => option.value === loggedInUser.userCode) || (
              loggedInUser.name
                ? {
                  value: loggedInUser.userCode,
                  label: loggedInUser.name !== loggedInUser.userCode ? `${loggedInUser.name} (${loggedInUser.userCode})` : loggedInUser.name,
                  name: loggedInUser.name
                }
                : null
            );
            setLoggedInUserOption(currentUserOption);
            setSelectedSalesPerson(currentUserOption);
          }
        }
      } catch (error) {
        if (active) setSalesPersonOptions([]);
      } finally {
        if (active) setSalesPersonLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [loggedInUser, salesPersonSearch]);

  const captureCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCoordinates({
          latitude: coords.latitude.toFixed(7),
          longitude: coords.longitude.toFixed(7)
        });
      },
      () => {
        setCoordinates(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    captureCurrentLocation();
  }, []);

  const handleReset = () => {
    formRef.current?.reset();
    setPhoto(null);
    setPhotoError('');
    setCoordinates(null);
    setSelectedSalesPerson(loggedInUserOption);
    setSalesPersonSearch('');
    setOtherBrand(false);
    setResetVersion((version) => version + 1);
    captureCurrentLocation();
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    setPhoto(null);
    setPhotoError('');

    if (!file) return;

    const nameParts = file.name.split('.');
    const extension = nameParts.length === 2 ? nameParts[1].toLowerCase() : '';
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const allowedMimeTypes = ['image/jpeg', 'image/png'];

    if (nameParts.length !== 2 || !nameParts[0] || !allowedExtensions.includes(extension)) {
      event.target.value = '';
      setPhotoError('Only a single .jpg, .jpeg or .png extension is allowed. Double extensions are not permitted.');
      return;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      event.target.value = '';
      setPhotoError('Only JPG, JPEG and PNG image files are allowed.');
      return;
    }

    const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a;
    const signatureMatches = (file.type === 'image/jpeg' && isJpeg) || (file.type === 'image/png' && isPng);

    if (!signatureMatches) {
      event.target.value = '';
      setPhotoError('The selected file is not a valid JPG or PNG image.');
      return;
    }

    setPhoto(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (photoError) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: photoError,
        confirmButtonText: 'OK',
      });
      return;
    }

    if (!selectedSalesPerson) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please select SSML Secondary Sales Person.',
        confirmButtonText: 'OK',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData(form);
      const selectedBrands = formData.getAll('brand[]').filter((brand) => brand !== 'Other');
      const customBrand = String(formData.get('other_brand_input') || '').trim();
      if (customBrand) selectedBrands.push(customBrand);
      formData.delete('brand[]');
      formData.delete('other_brand_input');
      selectedBrands.forEach((brand) => formData.append('brand[]', brand));
      const salesPersonName = selectedSalesPerson.name || selectedSalesPerson.label || '';
      formData.delete('sales_person_select');
      formData.set('sales_person', `${salesPersonName} (${selectedSalesPerson.value || ''})`);
      if (coordinates) {
        formData.set('latitude', coordinates.latitude);
        formData.set('longitude', coordinates.longitude);
      }
      const response = await insertSalesPersonVisit(formData);
      if (response?.success === true || String(response?.success).toLowerCase() === 'true') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response?.msg || 'Visit entry saved successfully.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e31e24'
        });
        handleReset();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.msg || 'Unable to save the visit entry.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e31e24'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Unable to save the visit entry.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#e31e24'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1180,
        mx: 'auto',
        pt: 8,
        pb: 5,
        '& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-focused': { color: APP_RED },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: APP_RED },
        '& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked': { color: APP_RED }
      }}
    >
      <Box component="form" ref={formRef} onSubmit={handleSubmit} noValidate={false}>
        <Stack spacing={2.25}>
          <Box sx={{ minHeight: 42, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h3" align="center" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
              Sales Person Entry for Visit
            </Typography>
          </Box>
          {sections.map((section, index) => (
            <SectionCard key={section.title} number={index + 1} title={section.title}>
              <Grid container spacing={2}>
                {section.fields.map((field) => field.name === 'sales_person' ? (
                  <Grid item xs={field.xs || 12} sm={field.sm || 6} key={field.name}>
                    <Select
                      name="sales_person_select"
                      value={selectedSalesPerson}
                      inputValue={salesPersonSearch}
                      onChange={(option, meta) => {
                        setSelectedSalesPerson(option);
                        if (meta.action === 'clear' || meta.action === 'select-option') {
                          setSalesPersonSearch('');
                        }
                      }}
                      onInputChange={(value, meta) => {
                        if (meta.action === 'input-change') setSalesPersonSearch(value);
                      }}
                      options={salesPersonOptions}
                      isDisabled
                      isSearchable={false}
                      isClearable={false}
                      isLoading={salesPersonLoading}
                      placeholder="Search Secondary Sales Person name or code"
                      noOptionsMessage={() => salesPersonLoading ? 'Loading...' : 'No sales person found'}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 40 }),
                        menu: (base) => ({ ...base, zIndex: 1000 })
                      }}
                    />
                  </Grid>
                ) : <Field key={field.name} field={field} resetVersion={resetVersion} />)}
              </Grid>
            </SectionCard>
          ))}

          <SectionCard number={3} title="Total Stock & Shop Sale">
            <Stack spacing={2.5}>{stockGroups.slice(0, 2).map((group) => <NumberGroup key={group.title} {...group} />)}</Stack>
          </SectionCard>

          <SectionCard number={4} title="SSML Brand Stock & Sale">
            <Stack spacing={2.5}>
              <Box>
                <FormLabel sx={{ color: APP_DARK, fontSize: 13, fontWeight: 700 }}>Brands Displayed in the Shop</FormLabel>
                <Stack key={`brands-${resetVersion}`} direction="row" flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                  {['Siyaram', 'Vimal', 'Raymond', 'Mitwa', 'Ashvira'].map((brand) => <FormControlLabel key={brand} sx={brandOptionSx} control={<Checkbox size="small" name="brand[]" value={brand} />} label={brand} />)}
                  <FormControlLabel
                    sx={brandOptionSx}
                    control={(
                      <Checkbox
                        size="small"
                        name="brand[]"
                        value="Other"
                        checked={otherBrand}
                        onChange={(event) => setOtherBrand(event.target.checked)}
                      />
                    )}
                    label="Other"
                  />
                </Stack>
                {otherBrand && (
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="other_brand_input"
                    label="Other brand name"
                    placeholder="Enter brand name"
                    sx={{ mt: 1.5 }}
                  />
                )}
              </Box>
              {stockGroups.slice(2, 4).map((group) => <NumberGroup key={group.title} {...group} />)}
            </Stack>
          </SectionCard>

          <SectionCard number={5} title="Thaan Stock Details">
            <Stack spacing={2.5}>{stockGroups.slice(4).map((group) => <NumberGroup key={group.title} {...group} />)}</Stack>
          </SectionCard>

          <SectionCard number={6} title="Quality, Feedback & Shop Photo">
            <Grid container spacing={2.5}>
              {[['colour_solidness', 'Colour Solidness & Sample Cutting Done?'], ['dealer_repo_feedback', 'Dealer Repo Feedback Given?']].map(([name, label]) => (
                <Grid item xs={12} sm={6} key={name}>
                  <FormLabel sx={{ color: `${APP_DARK} !important`, fontSize: 13, fontWeight: 700 }}>{label}</FormLabel>
                  <RadioGroup key={`${name}-${resetVersion}`} row name={name}>
                    <FormControlLabel value="Yes" sx={{ '& .MuiFormControlLabel-label': { color: `${APP_DARK} !important` } }} control={<Radio size="small" icon={radioIcon} checkedIcon={radioCheckedIcon} />} label="Yes" />
                    <FormControlLabel value="No" sx={{ '& .MuiFormControlLabel-label': { color: `${APP_DARK} !important` } }} control={<Radio size="small" icon={radioIcon} checkedIcon={radioCheckedIcon} />} label="No" />
                  </RadioGroup>
                </Grid>
              ))}
              <Grid item xs={12}><TextField fullWidth multiline minRows={3} name="feedback_notes" label="Feedback / Remarks" placeholder="Additional notes, complaints, or observations" /></Grid>
              <Grid item xs={12}>
                <Button component="label" fullWidth variant="outlined" startIcon={<CameraAltOutlinedIcon />} sx={{ minHeight: 86, borderStyle: 'dashed', color: APP_RED, borderColor: photoError ? 'error.main' : APP_RED, bgcolor: alpha(APP_RED, 0.025), '&:hover': { borderColor: APP_RED, bgcolor: alpha(APP_RED, 0.06) } }}>
                  {photo ? photo.name : 'Upload retailer shop photo'}
                  <input hidden type="file" name="shop_photo" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={handlePhotoChange} />
                </Button>
                {previewUrl && <Box component="img" src={previewUrl} alt="Shop preview" sx={{ display: 'block', mt: 1.5, width: 220, maxWidth: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }} />}
                <Typography variant="caption" display="block" color={photoError ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                  {photoError || 'Accepted formats: JPG, PNG. Photo is stored with this submission only.'}
                </Typography>
              </Grid>
            </Grid>
          </SectionCard>

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={1.5}>
            <Button variant="outlined" color="inherit" startIcon={<DeleteOutlineIcon />} onClick={handleReset}>Clear Form</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting} startIcon={<SaveOutlinedIcon />} sx={{ bgcolor: APP_RED, '&:hover': { bgcolor: '#bd171c' } }}>{isSubmitting ? 'Saving...' : 'Save Entry'}</Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default SalesPersonVisit;
